import { DiagnosticReport, Procedure } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import _ from "lodash";
import {
  getReferenceId,
  isReferenceDiagnosticReport,
} from "../../../shared-components/fhir-sections/shared";
import {
  getProcedureDate,
  getProcedureGroupingKey,
  groupResourcesByKey,
  ProcedureSeries,
} from "../../table-transforms/series-utils";
import { getProcedureType } from "./procedure-type";

export type ProcedureRowData = {
  id: string;
  procedure: string;
  datePerformed: string;
  status: string;
  originalData: ProcedureSeries;
  diagnosticReports: DiagnosticReport[];
  type: string;
  bodyRegion: string;
};

export function getProcedureRows({ bundle }: { bundle: FhirBundleSdk }): ProcedureRowData[] {
  const procedures = bundle.getProcedures();

  // Group procedures using standard series utility
  const procedureSeries = groupResourcesByKey(
    procedures,
    getProcedureGroupingKey,
    getProcedureDate,
    "Procedure"
  );

  // Convert series to row data
  return procedureSeries.map(series => {
    const mostRecentProcedure = series.resources[series.resources.length - 1];
    if (!mostRecentProcedure) {
      throw new Error("Series should always have at least one resource");
    }

    const diagnosticReports = getProcedureDiagnosticReports(mostRecentProcedure, bundle);
    const type = getProcedureType(mostRecentProcedure);
    const bodyRegion = getBodyRegionDisplay(mostRecentProcedure);

    // Get display name from the most recent procedure
    const displayName =
      mostRecentProcedure.code?.text ||
      mostRecentProcedure.code?.coding?.[0]?.display ||
      series.codeDisplay;

    return {
      id: mostRecentProcedure.id ?? "-",
      procedure: displayName,
      datePerformed: formatDate(
        mostRecentProcedure.performedDateTime || mostRecentProcedure.performedPeriod?.start
      ),
      status: mostRecentProcedure.status || "-",
      originalData: series,
      diagnosticReports,
      type,
      bodyRegion,
    };
  });
}

function getBodyRegionDisplay(procedure: Procedure): string {
  const bodySite = procedure.bodySite?.[0];
  if (!bodySite) return "-";

  // Prefer text field if available
  if (bodySite.text) {
    return cleanBodyRegionText(bodySite.text);
  }

  // Fall back to coding display
  const display = bodySite.coding?.[0]?.display;
  if (display) {
    return cleanBodyRegionText(display);
  }

  return "-";
}

function cleanBodyRegionText(text: string): string {
  // Remove common SNOMED suffixes to make display more concise
  return text
    .replace(/\s*\(body structure\)\s*$/i, "")
    .replace(/\s*\(specimen\)\s*$/i, "")
    .replace(/\s+structure$/i, "")
    .trim();
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";
  try {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return "-";
    return parsed.format(ISO_DATE);
  } catch {
    return "-";
  }
}

function getProcedureDiagnosticReports(
  procedure: Smart<Procedure>,
  bundle: FhirBundleSdk
): DiagnosticReport[] {
  if (!procedure.report) return [];
  return _(procedure.report)
    .filter(isReferenceDiagnosticReport)
    .map(getReferenceId)
    .map(id => bundle.getResourceById(id))
    .compact()
    .value() as DiagnosticReport[];
}
